// Per-route <head> metadata. React 19 hoists metadata tags, so any <meta> and
// <link> rendered here are moved into <head> automatically. The title and the
// description are the exception — index.html already ships one of each, so
// those two are edited in place instead; see useHeadText below.
// Inline JSON-LD <script> is rendered into the document and read by crawlers as-is.

import { useEffect } from 'react';

export const SITE_URL = 'https://test-demo-site-3ma.pages.dev';
const DEFAULT_OG = '/og-image.jpg';

/**
 * The tab title and the search-result description, changed in place.
 *
 * index.html already ships a <title> and a <meta name="description"> — the home
 * page's. Rendering a second pair would not replace them: React hoists what it
 * renders to the END of <head>, and a browser and a crawler both read the FIRST
 * title in the document, so this page would quietly wear the home page's title.
 * So edit the tags that are already there, and put their old values back when
 * this page unmounts — that way the home page keeps exactly what it has today,
 * whether a visitor lands on it or navigates back to it.
 */
function useHeadText(title: string, description: string) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content') ?? null;
    if (meta) meta.setAttribute('content', description);

    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== null) meta.setAttribute('content', previousDescription);
    };
  }, [title, description]);
}

/**
 * @param title      the page title, already including the business name
 * @param description one or two sentences, under ~160 characters
 * @param path       the route this renders on, e.g. '/about'
 * @param image      absolute or root-relative OG image
 * @param jsonLd     structured data for this page, if any
 */
export function Seo({
  title,
  description,
  path,
  image = DEFAULT_OG,
  jsonLd,
}: {
  title: string;
  description: string;
  path: string;
  image?: string;
  jsonLd?: object;
}) {
  const url = SITE_URL + (path === '/' ? '/' : path);
  const ogImage = image.startsWith('http') ? image : SITE_URL + image;
  useHeadText(title, description);

  return (
    <>
      <link data-seo="" rel="canonical" href={url} />

      <meta data-seo="" property="og:title" content={title} />
      <meta data-seo="" property="og:description" content={description} />
      <meta data-seo="" property="og:type" content="website" />
      <meta data-seo="" property="og:url" content={url} />
      <meta data-seo="" property="og:image" content={ogImage} />
      <meta data-seo="" property="og:site_name" content="Demo Customer" />

      <meta data-seo="" name="twitter:card" content="summary_large_image" />
      <meta data-seo="" name="twitter:title" content={title} />
      <meta data-seo="" name="twitter:description" content={description} />
      <meta data-seo="" name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </>
  );
}
